import { API_BASE } from '../config';

/**
 * Look up a product by UPC barcode.
 * Uses fetchWithAuth for automatic token refresh + 401 retry.
 * Returns normalized product object matching ProductResponse schema.
 *
 * @param {Function} fetchWithAuth - Auth-aware fetch from useFetchWithAuth hook
 * @param {string} barcode - UPC barcode (8-14 digits)
 * @param {Object} options - { retryCount, signal }
 * @returns {Object} { found, product?, message?, error?, source?, cached?, product_cache_id? }
 */
export async function lookupProductByUPC(fetchWithAuth, barcode, options = {}) {
  const { retryCount = 0, signal } = options;

  if (!barcode || !barcode.trim()) {
    return { found: false, error: 'No barcode provided' };
  }

  try {
    const response = await fetchWithAuth(
      `${API_BASE}/api/products/lookup/upc/${barcode.trim()}`,
      { method: 'GET', signal }
    );

    if (!response.ok) {
      // Retry for server errors (matching PantryForms pattern)
      if (retryCount < 2 && (response.status >= 500 || response.status === 429)) {
        await new Promise(r => setTimeout(r, 1000 * (retryCount + 1)));
        return lookupProductByUPC(fetchWithAuth, barcode, { retryCount: retryCount + 1, signal });
      }
      return { found: false, error: `Lookup failed: ${response.status}` };
    }

    const data = await response.json();

    if (data.found && data.product) {
      return {
        found: true,
        source: data.source,
        cached: data.cached,
        product: data.product,
        product_cache_id: data.product_cache_id,
      };
    }

    return {
      found: false,
      message: data.message || 'Product not found in database'
    };
  } catch (err) {
    // Retry for network errors (matching PantryForms pattern)
    if (retryCount < 2 && (err.name === 'AbortError' || err.message?.includes('fetch'))) {
      await new Promise(r => setTimeout(r, 1000 * (retryCount + 1)));
      return lookupProductByUPC(fetchWithAuth, barcode, { retryCount: retryCount + 1, signal });
    }
    return { found: false, error: err.message };
  }
}
